import * as React from "react";
import { LabourHour, LabourHourLineItem } from "@interfaces/labourHour";
import { decodeLabourHour, encodeLabourHour } from "decoders/labourHour";
import {
  deleteBatchApiData,
  getApiData,
  listApiData,
  postApiData,
  postBatchApiData,
} from "@hooks/utils/api";

export const useLabourHour = (reportId?: string) => {
  const [labourHours, setLabourHours] = React.useState<LabourHourLineItem[]>(
    []
  );
  const [isLabourHourLoading, setIsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  const getLabourHour = async (
    id: string
  ): Promise<LabourHourLineItem | undefined> => {
    try {
      setIsLoading(true);
      const labourHourResult = await getApiData(
        "getLabourHour",
        "labourHour",
        id
      );
      if (Object.keys(labourHourResult.data).length === 0) {
        return undefined;
      }
      return labourHourResult
        ? decodeLabourHour(labourHourResult.data as LabourHour)
        : undefined;
    } catch (e: any) {
      setError("Could not fetch labourHour");
    } finally {
      setIsLoading(false);
    }
  };

  const listLabourHours = async (reportId: string): Promise<void> => {
    try {
      setIsLoading(true);
      const labourHourResult = await listApiData(
        "listLabourHours",
        "labourHour",
        reportId
      );
      const labourHours = labourHourResult.data.map((p) =>
        decodeLabourHour(p as LabourHour)
      );

      setLabourHours(labourHours);
    } catch (e: any) {
      setError("Could not list labourHours");
    } finally {
      setIsLoading(false);
    }
  };

  const searchLabourHours = async (
    query: string
  ): Promise<LabourHourLineItem[]> => {
    try {
      const labourHourResult = await listApiData(
        "searchLabourHours",
        "labourHour",
        query
      );
      return labourHourResult.data.map((p) =>
        decodeLabourHour(p as LabourHour)
      );
    } catch (e: any) {
      setError("Could not search labourHours");
      return [] as LabourHourLineItem[];
    }
  };

  const createLabourHour = async (
    labourHour: LabourHourLineItem
  ): Promise<LabourHourLineItem> => {
    try {
      setIsLoading(true);
      const encodedLabourHour = encodeLabourHour(labourHour);
      const upsertedLabourHour = await postApiData(
        "createLabourHour",
        "labourHour",
        encodedLabourHour
      );
      const decodedLabourHour = decodeLabourHour(
        upsertedLabourHour.data as LabourHour
      );
      setLabourHours([...labourHours, decodedLabourHour]);
      return decodedLabourHour;
    } catch (e: any) {
      setError("Could not list labourHours");
    } finally {
      setIsLoading(false);
    }
    return labourHour;
  };

  const updateLabourHour = async (
    labourHour: LabourHourLineItem
  ): Promise<void> => {
    try {
      setIsLoading(true);
      const encodedLabourHour = encodeLabourHour(labourHour);
      await postApiData("updateLabourHour", "labourHour", encodedLabourHour);
      setLabourHours(
        labourHours.map((c) => {
          if (c.id === labourHour.id) {
            return labourHour;
          }
          return c;
        })
      );
    } catch (e: any) {
      setError("Could not update labourHour");
    } finally {
      setIsLoading(false);
    }
  };

  const upsertLabourHours = async (
    labourHoursToUpsert: LabourHourLineItem[]
  ): Promise<void> => {
    try {
      setIsLoading(true);
      const encodedLabourHours = labourHoursToUpsert.map((e) =>
        encodeLabourHour(e)
      );
      await postBatchApiData(
        "upsertLabourHours",
        "labourHour",
        encodedLabourHours
      );

      const updatedLabourHours = labourHours.reduce((acc, labourHour) => {
        const updatedLabourHour = labourHoursToUpsert.find(
          (e) => e.id === labourHour.id
        );
        if (updatedLabourHour) {
          return [...acc, updatedLabourHour];
        }
        return [...acc, labourHour];
      }, [] as LabourHourLineItem[]);

      const createdLabourHours = labourHoursToUpsert.filter((labourHour) => {
        return !updatedLabourHours.find((lh) => lh.id === labourHour.id);
      });

      setLabourHours([...updatedLabourHours, ...createdLabourHours]);
    } catch (e: any) {
      setError("Could not update labour hour");
    } finally {
      setIsLoading(false);
    }
  };

  const deleteLabourHours = async (
    labourHoursToDelete: LabourHourLineItem[]
  ): Promise<void> => {
    try {
      setIsLoading(true);
      const encodedLabourHour = labourHoursToDelete.map((e) =>
        encodeLabourHour(e)
      );
      await deleteBatchApiData(
        "deleteLabourHours",
        "labourHour",
        encodedLabourHour
      );

      const deletedRowIds = labourHoursToDelete.map((e) => e.id);
      setLabourHours(
        labourHours.filter((e) => {
          return !deletedRowIds.includes(e.id);
        })
      );
    } catch (e: any) {
      setError("Could not delete labourHour");
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (reportId) {
      listLabourHours(reportId);
    }
  }, [reportId]);

  return {
    labourHours,
    getLabourHour,
    listLabourHours,
    createLabourHour,
    updateLabourHour,
    deleteLabourHours,
    upsertLabourHours,
    searchLabourHours,
    isLabourHourLoading,
    error,
  };
};
